import React, { useState, useCallback } from 'react';
import Modal from 'react-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUser, faSignOutAlt, faArrowLeft,
    faChevronDown, faChevronUp, faEdit,
    faEye, faEyeSlash, faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import { Link as Anchor } from 'react-router-dom';
import baseURL from '../url';
import RecoverModal from '../RecoverModal/RecoverModal';
import './NavbarCliente.css';

export default function NavbarCliente() {
    /* ── Estado global del cliente ─────────────────────────── */
    const [cliente, setCliente] = useState(() => {
        const saved = localStorage.getItem('cliente');
        return saved ? JSON.parse(saved) : null;
    });

    /* ── Función para sincronizar con el servidor ──────────── */
    const sincronizar = useCallback(() => {
        const saved = localStorage.getItem('cliente');
        if (!saved) return;
        const { idCliente } = JSON.parse(saved);
        fetch(`${baseURL}/clienteGet.php?idCliente=${idCliente}`)
            .then(r => r.json())
            .then(data => {
                if (data.cliente) {
                    const current = JSON.parse(localStorage.getItem('cliente'));
                    if (JSON.stringify(current) !== JSON.stringify(data.cliente)) {
                        localStorage.setItem('cliente', JSON.stringify(data.cliente));
                        setCliente(data.cliente);
                    }
                }
            })
            .catch(() => {});
    }, []);

    /* ── Efecto para sincronización al recibir foco / visibilidad ── */
    React.useEffect(() => {
        sincronizar(); // al montar

        const onVisibility = () => {
            if (document.visibilityState === 'visible') sincronizar();
        };

        window.addEventListener('focus', sincronizar);
        document.addEventListener('visibilitychange', onVisibility);

        return () => {
            window.removeEventListener('focus', sincronizar);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [sincronizar]);

    /* ── Efecto para polling cada 30 segundos ───────────────── */
    React.useEffect(() => {
        if (!cliente) return;
        const intervalId = setInterval(sincronizar, 30000);
        return () => clearInterval(intervalId);
    }, [cliente, sincronizar]);

    /* ── SESIÓN 6: Sync de logout entre pestañas ────────────────
       Si otra pestaña dispara el evento 'logout-event' en localStorage,
       esta pestaña recarga para limpiar el estado del cliente.        */
    React.useEffect(() => {
        const syncLogout = (e) => {
            if (e.key === 'logout-event') {
                window.location.reload();
            }
        };
        window.addEventListener('storage', syncLogout);
        return () => window.removeEventListener('storage', syncLogout);
    }, []);

    /* ── Modal auth (login / registro / recuperar) ────────── */
    const [modalOpen, setModalOpen] = useState(false);
    const [tab, setTab] = useState('login');

    /* ── Modal editar perfil ───────────────────────────────── */
    const [editOpen, setEditOpen] = useState(false);
    const [editNombre, setEditNombre] = useState('');
    const [editTelefono, setEditTelefono] = useState('');
    const [editPassActual, setEditPassActual] = useState('');
    const [editPassNueva, setEditPassNueva] = useState('');
    const [verPassActual, setVerPassActual] = useState(false);
    const [verPassNueva, setVerPassNueva] = useState(false);
    const [editError, setEditError] = useState('');
    const [editExito, setEditExito] = useState('');
    const [editLoading, setEditLoading] = useState(false);
    const [fotoPreview, setFotoPreview] = useState('');
    const [fotoFile, setFotoFile] = useState(null);
    const fotoInputRef = React.useRef(null);

    /* ── Campos formulario auth ────────────────────────────── */
    const [nombre, setNombre] = useState('');
    const [email, setEmail] = useState('');
    const [pass, setPass] = useState('');
    const [telefono, setTelefono] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    /* ── Dropdown ──────────────────────────────────────────── */
    const [dropOpen, setDropOpen] = useState(false);

    /* ── Helpers auth ──────────────────────────────────────── */
    const resetForm = () => {
        setNombre(''); setEmail(''); setPass(''); setTelefono(''); setError('');
    };
    const abrirModal = (tabInicial = 'login') => {
        resetForm(); setTab(tabInicial); setModalOpen(true);
    };
    const cerrarModal = () => { setModalOpen(false); resetForm(); };

    /* ── Abrir modal editar perfil ─────────────────────────── */
    const abrirEditar = () => {
        setEditNombre(cliente.nombre || '');
        setEditTelefono(cliente.telefono || '');
        setEditPassActual('');
        setEditPassNueva('');
        setEditError('');
        setEditExito('');
        setFotoPreview('');
        setFotoFile(null);
        setDropOpen(false);
        setEditOpen(true);
    };

    /* ── Seleccionar foto nueva ────────────────────────────── */
    const handleFotoChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (ev) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const MAX = 400;
                let w = img.width, h = img.height;
                if (w > h) { if (w > MAX) { h = h * MAX / w; w = MAX; } }
                else { if (h > MAX) { w = w * MAX / h; h = MAX; } }
                canvas.width = w;
                canvas.height = h;
                canvas.getContext('2d').drawImage(img, 0, 0, w, h);
                canvas.toBlob(blob => {
                    const comprimido = new File([blob], file.name, { type: 'image/jpeg' });
                    setFotoFile(comprimido);
                    setFotoPreview(canvas.toDataURL('image/jpeg', 0.8));
                }, 'image/jpeg', 0.8);
            };
            img.src = ev.target.result;
        };
        reader.readAsDataURL(file);
    };

    /* ── Guardar cambios de perfil ─────────────────────────── */
    const handleGuardarPerfil = async () => {
        setEditError('');
        setEditExito('');
        if (!editNombre.trim()) { setEditError('El nombre no puede estar vacío'); return; }

        setEditLoading(true);
        try {
            let fotoActualizada = cliente.foto || '';
            if (fotoFile) {
                const fdFoto = new FormData();
                fdFoto.append('idCliente', cliente.idCliente);
                fdFoto.append('foto', fotoFile);
                const resFoto = await fetch(`${baseURL}/userFotoPut.php`, { method: 'POST', body: fdFoto });
                const dataFoto = await resFoto.json();
                if (dataFoto.success) {
                    fotoActualizada = dataFoto.foto;
                } else {
                    setEditError(dataFoto.error || 'Error al subir la foto');
                    setEditLoading(false);
                    return;
                }
            }

            const fd = new FormData();
            fd.append('idCliente', cliente.idCliente);
            fd.append('nombre', editNombre.trim());
            fd.append('telefono', editTelefono.trim());
            fd.append('contrasena_actual', editPassActual);
            fd.append('nueva_contrasena', editPassNueva);

            const res = await fetch(`${baseURL}/clienteUpdatePost.php`, { method: 'POST', body: fd });
            const data = await res.json();

            if (data.ok) {
                const clienteActualizado = { ...cliente, ...data.cliente, foto: fotoActualizada };
                localStorage.setItem('cliente', JSON.stringify(clienteActualizado));
                setCliente(clienteActualizado);
                setEditExito('¡Datos actualizados correctamente!');
                setTimeout(() => setEditOpen(false), 1600);
            } else {
                setEditError(data.error || 'Error al actualizar los datos');
            }
        } catch {
            setEditError('Error de conexión. Intenta de nuevo.');
        }
        setEditLoading(false);
    };

    /* ── Login ─────────────────────────────────────────────── */
    const handleLogin = async () => {
        setError('');
        if (!email || !pass) { setError('Ingresa tu correo y contraseña'); return; }
        setLoading(true);
        try {
            const fd = new FormData();
            fd.append('email', email);
            fd.append('contrasena', pass);
            const res = await fetch(`${baseURL}/clienteLoginPost.php`, { method: 'POST', body: fd });
            const data = await res.json();
            if (data.cliente) {
                localStorage.setItem('cliente', JSON.stringify(data.cliente));
                setCliente(data.cliente);
                cerrarModal();
            } else {
                setError(data.error || 'Correo o contraseña incorrectos');
            }
        } catch {
            setError('Error de conexión. Intenta de nuevo.');
        }
        setLoading(false);
    };

    /* ── Registro ──────────────────────────────────────────── */
    const handleRegistro = async () => {
        setError('');
        if (!nombre || !email || !pass) { setError('Completa los campos obligatorios (*)'); return; }
        if (pass.length < 6) { setError('La contraseña debe tener al menos 6 caracteres'); return; }
        setLoading(true);
        try {
            const fd = new FormData();
            fd.append('nombre', nombre);
            fd.append('email', email);
            fd.append('contrasena', pass);
            fd.append('telefono', telefono);
            const res = await fetch(`${baseURL}/clienteRegistroPost.php`, { method: 'POST', body: fd });
            const data = await res.json();
            if (data.cliente) {
                localStorage.setItem('cliente', JSON.stringify(data.cliente));
                setCliente(data.cliente);
                cerrarModal();
            } else {
                setError(data.error || 'Error al registrarse');
            }
        } catch {
            setError('Error de conexión. Intenta de nuevo.');
        }
        setLoading(false);
    };

    /* ── Logout ────────────────────────────────────────────── */
    const handleLogout = () => {
        localStorage.removeItem('cliente');
        setDropOpen(false);
        /* SESIÓN 6: broadcast a otras pestañas abiertas */
        localStorage.setItem('logout-event', Date.now().toString());
        window.location.reload();
    };

    return (
        <div className="nc-wrap">
            {!cliente ? (
                <div className="nc-guest-btns">
                    <button className="nc-btn-outline" onClick={() => abrirModal('registro')}>
                        Registrarse
                    </button>
                    <button className="nc-btn-solid" onClick={() => abrirModal('login')}>
                        <FontAwesomeIcon icon={faUser} /> Iniciar sesión
                    </button>
                </div>
            ) : (
                <div className="nc-user-wrap">
                    <button className="nc-user-btn" onClick={() => setDropOpen(p => !p)}>
                        <span className="nc-avatar">
                            {cliente.foto ? (
                                <img
                                    src={cliente.foto.startsWith('data:image') ? cliente.foto : `${cliente.foto}?t=${Date.now()}`}
                                    alt={cliente.nombre}
                                    className="nc-avatar-img"
                                />
                            ) : (
                                cliente.nombre?.charAt(0).toUpperCase()
                            )}
                        </span>
                        <span className="nc-username">{cliente.nombre?.split(' ')[0]}</span>
                        <FontAwesomeIcon icon={dropOpen ? faChevronUp : faChevronDown} className="nc-chevron" />
                    </button>

                    {dropOpen && (
                        <div className="nc-dropdown">
                            <p className="nc-drop-greeting">
                                Hola, <strong>{cliente.nombre}</strong>
                            </p>
                            <hr className="nc-divider" />
                            <button className="nc-drop-link" onClick={abrirEditar}>
                                <FontAwesomeIcon icon={faEdit} /> Editar perfil
                            </button>
                            <hr className="nc-divider" />
                            {/* SESIÓN 6: link a Mis Descargas */}
                            <Anchor
                                className="nc-drop-link"
                                to="/perfil?tab=descargas"
                                onClick={() => setDropOpen(false)}
                            >
                                📥 Mis Descargas
                            </Anchor>
                            <hr className="nc-divider" />
                            <button className="nc-drop-logout" onClick={handleLogout}>
                                <FontAwesomeIcon icon={faSignOutAlt} /> Cerrar sesión
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Modal Login / Registro / Recuperar */}
            <Modal
                isOpen={modalOpen}
                className="nc-modal"
                overlayClassName="nc-overlay"
                onRequestClose={cerrarModal}
            >
                {tab !== 'recuperar' && (
                    <div className="nc-modal-avatar">
                        <FontAwesomeIcon icon={faUser} />
                    </div>
                )}
                <div className="nc-modal-header">
                    <button className="nc-back-btn" onClick={cerrarModal}>
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </button>
                    <h4>
                        {tab === 'login' ? 'Iniciar sesión'
                        : tab === 'registro' ? 'Crear cuenta'
                        : 'Recuperar contraseña'}
                    </h4>
                    <div />
                </div>
                {tab !== 'recuperar' && (
                    <div className="nc-tabs">
                        <button
                            className={`nc-tab ${tab === 'login' ? 'activo' : ''}`}
                            onClick={() => { setTab('login'); setError(''); }}
                        >
                            Iniciar sesión
                        </button>
                        <button
                            className={`nc-tab ${tab === 'registro' ? 'activo' : ''}`}
                            onClick={() => { setTab('registro'); setError(''); }}
                        >
                            Crear cuenta
                        </button>
                    </div>
                )}
                <div className="nc-form">
                    {tab === 'recuperar' ? (
                        <RecoverModal
                            onVolver={() => { setTab('login'); setError(''); }}
                            onExito={() => { setTab('login'); setError(''); }}
                        />
                    ) : (
                        <>
                            {tab === 'registro' && (
                                <>
                                    <input
                                        type="text"
                                        placeholder="Nombre completo (*)"
                                        value={nombre}
                                        onChange={e => setNombre(e.target.value)}
                                    />
                                    <input
                                        type="number"
                                        placeholder="Teléfono / WhatsApp (opcional)"
                                        value={telefono}
                                        onChange={e => setTelefono(e.target.value)}
                                    />
                                </>
                            )}
                            <input
                                type="email"
                                placeholder="Correo electrónico (*)"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                            />
                            <input
                                type="password"
                                placeholder="Contraseña (*) mínimo 6 caracteres"
                                value={pass}
                                onChange={e => setPass(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && (tab === 'login' ? handleLogin() : handleRegistro())}
                            />
                            {tab === 'login' && (
                                <button
                                    type="button"
                                    className="nc-forgot-btn"
                                    onClick={() => { setTab('recuperar'); setError(''); }}
                                >
                                    ¿Olvidaste tu contraseña?
                                </button>
                            )}
                            {error && <p className="nc-error">{error}</p>}
                        </>
                    )}
                </div>
                {tab !== 'recuperar' && (
                    <button
                        className="nc-submit-btn"
                        onClick={tab === 'login' ? handleLogin : handleRegistro}
                        disabled={loading}
                    >
                        {loading ? 'Procesando...' : tab === 'login' ? 'Ingresar' : 'Registrarme'}
                    </button>
                )}
                {tab !== 'recuperar' && (
                    <p className="nc-switch-tab">
                        {tab === 'login' ? (
                            <>¿No tienes cuenta?{' '}
                                <button onClick={() => { setTab('registro'); setError(''); }}>
                                    Regístrate aquí
                                </button>
                            </>
                        ) : (
                            <>¿Ya tienes cuenta?{' '}
                                <button onClick={() => { setTab('login'); setError(''); }}>
                                    Inicia sesión
                                </button>
                            </>
                        )}
                    </p>
                )}
            </Modal>

            {/* Modal Editar Perfil */}
            <Modal
                isOpen={editOpen}
                className="nc-modal"
                overlayClassName="nc-overlay"
                onRequestClose={() => setEditOpen(false)}
            >
                <div
                    className="nc-modal-avatar nc-modal-avatar--editable"
                    onClick={() => fotoInputRef.current?.click()}
                    title="Cambiar foto"
                >
                    {(fotoPreview || cliente?.foto) ? (
                        <img
                            src={fotoPreview || (cliente.foto && cliente.foto.startsWith('data:image') ? cliente.foto : `${cliente.foto}?t=${Date.now()}`)}
                            alt="foto perfil"
                            className="nc-avatar-foto"
                        />
                    ) : (
                        <span className="nc-avatar-inicial">
                            {cliente?.nombre?.charAt(0).toUpperCase()}
                        </span>
                    )}
                    <span className="nc-avatar-edit-icon">
                        <FontAwesomeIcon icon={faEdit} />
                    </span>
                </div>
                <input
                    ref={fotoInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleFotoChange}
                />
                <div className="nc-modal-header">
                    <button className="nc-back-btn" onClick={() => setEditOpen(false)}>
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </button>
                    <h4>Editar perfil</h4>
                    <div />
                </div>
                <div className="nc-form">
                    <div className="nc-edit-email-wrap">
                        <label>Correo electrónico</label>
                        <p className="nc-edit-email">{cliente?.email}</p>
                    </div>
                    <input
                        type="text"
                        placeholder="Nombre completo (*)"
                        value={editNombre}
                        onChange={e => setEditNombre(e.target.value)}
                    />
                    <input
                        type="number"
                        placeholder="Teléfono / WhatsApp"
                        value={editTelefono}
                        onChange={e => setEditTelefono(e.target.value)}
                    />
                    <p className="nc-edit-section-label">Cambiar contraseña <span>(opcional)</span></p>
                    <div className="nc-pass-field">
                        <input
                            type={verPassActual ? 'text' : 'password'}
                            placeholder="Contraseña actual"
                            value={editPassActual}
                            onChange={e => setEditPassActual(e.target.value)}
                        />
                        <button type="button" className="nc-eye-btn" onClick={() => setVerPassActual(p => !p)}>
                            <FontAwesomeIcon icon={verPassActual ? faEyeSlash : faEye} />
                        </button>
                    </div>
                    <div className="nc-pass-field">
                        <input
                            type={verPassNueva ? 'text' : 'password'}
                            placeholder="Nueva contraseña (mín. 6 caracteres)"
                            value={editPassNueva}
                            onChange={e => setEditPassNueva(e.target.value)}
                        />
                        <button type="button" className="nc-eye-btn" onClick={() => setVerPassNueva(p => !p)}>
                            <FontAwesomeIcon icon={verPassNueva ? faEyeSlash : faEye} />
                        </button>
                    </div>
                    {editError && <p className="nc-error">{editError}</p>}
                    {editExito && (
                        <p className="nc-exito">
                            <FontAwesomeIcon icon={faCheckCircle} /> {editExito}
                        </p>
                    )}
                </div>
                <button
                    className="nc-submit-btn"
                    onClick={handleGuardarPerfil}
                    disabled={editLoading}
                >
                    {editLoading ? 'Guardando...' : 'Guardar cambios'}
                </button>
            </Modal>
        </div>
    );
}